import { makeQueryOptions } from '@mint-lib/api-zod-support/react-query';

import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  CreateDashboardRequestSchema,
  CreateDashboardsListSettingsRequestSchema,
  CreatePivotListSettingsRequestSchema,
  CreateViewRequestSchema,
  DashboardSchema,
  DashboardSettingsParamsSchema,
  DashboardsListSettingsResponseSchema,
  DashboardsListSettingsSchema,
  DashboardsResponseSchema,
  DashboardTypeQuerySchema,
  DeleteDashboardResponseSchema,
  DeleteDashboardsListSettingsResponseSchema,
  DeleteViewResponseSchema,
  ExportPivotTableFlowChartRequestSchema,
  GridDataRequestSchema,
  GridDataResponseSchema,
  GridDimensionsResponseSchema,
  GridMetricsResponseSchema,
  PivotCurrenciesResponseSchema,
  PivotDimensionsResponseSchema,
  PivotDimensionUniquesRequestSchema,
  PivotDimensionUniquesResponseSchema,
  PivotFrequencySubRowsResponseSchema,
  PivotListSettingsResponseSchema,
  PivotListSettingsSchema,
  PivotMetricsResponseSchema,
  PivotReachSubRowsRequestSchema,
  PivotReachSubRowsResponseSchema,
  PivotSettingsParamsSchema,
  PivotTableComparisonRequestSchema,
  PivotTableRequestSchema,
  PivotTableResponseSchema,
  PivotTypeQuerySchema,
  SelectedCurrencySchema,
  SharedUsersResponseSchema,
  UpdateDashboardRequestSchema,
  UpdatePermissionRequestSchema,
  UpdateViewRequestSchema,
  UserCurrencyResponseSchema,
  ViewQuerySchema,
  ViewsQuerySchema,
  ViewsResponseSchema,
  WidgetDataExportRequestSchema,
  WidgetDataExportResponseSchema,
  WidgetDataRequestSchema,
  WidgetDataRequestSchemaSnakeCase,
  WidgetDataResponseSchema,
} from '../shared/schemas/index.js';
import {
  PivotTableExportRequestSchema,
  PivotTableExportResponseSchema,
} from '../shared/schemas/pivotTable.js';
import {
  API_PREFIX,
  BFF_DASHBOARDS,
  BFF_DASHBOARDS_V2,
  COMPANY_INFO,
  COMPANY_INFO_V2_MINT2,
  DASHBOARDS,
  DASHBOARDS_INSIGHTS_HUB_VIEW_BY_DASHBOARD_ID,
  DASHBOARDS_INSIGHTS_HUB_VIEW_BY_ID,
  DASHBOARDS_INSIGHTS_HUB_VIEW_SHARE_BY_ID,
  DASHBOARDS_SETTINGS,
  DASHBOARDS_V2_SETTINGS,
  DASHBOARDS_V2_VIEW,
  DIMENSIONS_UNIQUES,
  EXPORT_CSV_V2_MINT2,
  EXPORT_PIVOT_V2_MINT2,
  EXPORT_TABLE_FLOWCHART,
  GET_COMPARISON_TABLE,
  GET_COMPARISON_TABLE_OUTDATED,
  GET_COMPARISON_TABLE_V2_MINT2,
  GET_CURRENCIES_V2_MINT2,
  GET_DIMENSIONS_UNIQUES_V2_MINT2,
  GET_DIMENSIONS_V2,
  GET_DIMENSIONS_V2_MINT2,
  GET_FREQUENCY_SUBROWS,
  GET_METRICS,
  GET_METRICS_V2_MINT2,
  GET_REACH_SUBROWS,
  GET_SELECTED_CURRENCY,
  GET_TABLE,
  GET_TABLE_OUTDATED,
  GET_TABLE_V2_MINT2,
  GET_USER_CURRENCIES,
  GRID_DATA,
  GRID_DIMENSIONS,
  GRID_METRICS,
  WIDGET_DATA,
  WIDGET_DATA_EXPORT,
  WIDGET_DATA_V2_MINT2,
} from '../shared/urls.js';

export type DashboardType = z.infer<typeof DashboardSchema>;
export type DashboardsResponseType = z.infer<typeof DashboardsResponseSchema>;
export type DashboardsListResult = DashboardsResponseType['data'];
export type CreateDashboardRequestType = z.infer<
  typeof CreateDashboardRequestSchema
>;
export type UpdateDashboardRequestType = z.infer<
  typeof UpdateDashboardRequestSchema
>;
export type DashboardsListSettingsType = z.infer<
  typeof DashboardsListSettingsSchema
>;
export type DashboardsListSettingsResponseType = z.infer<
  typeof DashboardsListSettingsResponseSchema
>;
export type PivotListSettingsType = z.infer<typeof PivotListSettingsSchema>;
export type PivotListSettingsResponseType = z.infer<
  typeof PivotListSettingsResponseSchema
>;
export type DashboardSettingsParamsType = z.infer<
  typeof DashboardSettingsParamsSchema
>;
export type PivotTableRequestType = z.infer<typeof PivotTableRequestSchema>;
export type PivotTableResponseType = z.infer<typeof PivotTableResponseSchema>;
export type PivotMetricsResponseType = z.infer<
  typeof PivotMetricsResponseSchema
>;
export type PivotDimensionsResponseType = z.infer<
  typeof PivotDimensionsResponseSchema
>;
export type PivotReachSubRowsRequestType = z.infer<
  typeof PivotReachSubRowsRequestSchema
>;
export type PivotReachSubRowsResponseType = z.infer<
  typeof PivotReachSubRowsResponseSchema
>;
export type PivotFrequencySubRowsResponseType = z.infer<
  typeof PivotFrequencySubRowsResponseSchema
>;
export type SelectedCurrency = z.infer<typeof SelectedCurrencySchema>;
export type UserCurrencyResponseType = z.infer<
  typeof UserCurrencyResponseSchema
>;
export type PivotCurrenciesResponseType = z.infer<
  typeof PivotCurrenciesResponseSchema
>;
export type PivotDimensionUniquesRequestType = z.infer<
  typeof PivotDimensionUniquesRequestSchema
>;
export type PivotDimensionUniquesResponseType = z.infer<
  typeof PivotDimensionUniquesResponseSchema
>;
export type WidgetDataRequestType = z.infer<typeof WidgetDataRequestSchema>;
export type WidgetDataResponseType = z.infer<typeof WidgetDataResponseSchema>;
export type WidgetDataExportRequestType = z.infer<
  typeof WidgetDataExportRequestSchema
>;
export type WidgetDataExportResponseType = z.infer<
  typeof WidgetDataExportResponseSchema
>;

export type GridDataRequestType = z.infer<typeof GridDataRequestSchema>;
export type GridDataResponseType = z.infer<typeof GridDataResponseSchema>;
export type GridDimensionsResponseType = z.infer<
  typeof GridDimensionsResponseSchema
>;

export type GridMetricsResponseType = z.infer<typeof GridMetricsResponseSchema>;

export type PivotTableExportRequestType = z.infer<
  typeof PivotTableExportRequestSchema
>;

export type PivotTableExportResponseType = z.infer<
  typeof PivotTableExportResponseSchema
>;

// Re-export View types from schemas
export type {
  CreateViewRequest,
  GetSharedUsersResponse,
  SharedUser,
  UpdatePermissionRequest,
  UpdateViewRequest,
  View,
  ViewType,
} from '../shared/schemas/index.js';

// Re-export helper functions
export { convertSharedUsersResponse } from '../shared/schemas/index.js';

// Re-export endpoint constants for direct usage
export {
  API_BFF_DASHBOARDS,
  API_COMPANY_INFO,
  API_DASHBOARDS,
  API_DASHBOARDS_INSIGHTS_HUB_VIEW_SHARE_BY_ID,
  API_DASHBOARDS_SETTINGS,
  API_DIMENSIONS_UNIQUES,
  API_GET_COMPARISON_TABLE,
  API_GET_COMPARISON_TABLE_OUTDATED,
  API_GET_DIMENSIONS,
  API_GET_FREQUENCY_SUBROWS,
  API_GET_DIMENSIONS_V2,
  API_GET_METRICS,
  API_GET_METRICS_COMPANY_ID,
  API_GET_REACH_SUBROWS,
  API_GET_TABLE,
  API_GET_TABLE_OUTDATED,
  API_GET_USER_CURRENCIES,
  API_GET_USER_CURRENCIES_COMPANY_ID,
  API_PREFIX,
  API_WIDGET_DATA,
  API_GRID_DATA,
  API_GRID_DIMENSIONS,
  API_GRID_METRICS,
} from '../shared/urls.js';
export {
  PIVOT_BASE,
  INSIGHTS_HUB_BASE,
  BFF_DASHBOARDS,
  BFF_DASHBOARDS_V2,
  COMPANY_INFO,
  COMPANY_INFO_V2_MINT2,
  DASHBOARDS,
  DASHBOARDS_INSIGHTS_HUB_VIEW_BY_DASHBOARD_ID,
  DASHBOARDS_INSIGHTS_HUB_VIEW_BY_ID,
  DASHBOARDS_INSIGHTS_HUB_VIEW_SHARE_BY_ID,
  DASHBOARDS_SETTINGS,
  DASHBOARDS_V2_SETTINGS,
  DASHBOARDS_V2_VIEW,
  DIMENSIONS_UNIQUES,
  EXPORT_CSV_V2_MINT2,
  GET_COMPARISON_TABLE,
  GET_COMPARISON_TABLE_OUTDATED,
  GET_COMPARISON_TABLE_V2_MINT2,
  GET_CURRENCIES_V2_MINT2,
  GET_DIMENSIONS,
  GET_DIMENSIONS_V2,
  GET_DIMENSIONS_V2_MINT2,
  GET_DIMENSIONS_UNIQUES_V2_MINT2,
  GET_FREQUENCY_SUBROWS,
  GET_METRICS,
  GET_METRICS_V2_MINT2,
  GET_REACH_SUBROWS,
  GET_SELECTED_CURRENCY,
  GET_TABLE,
  GET_TABLE_OUTDATED,
  GET_TABLE_V2_MINT2,
  GET_USER_CURRENCIES,
  GRID_DATA,
  GRID_DIMENSIONS,
  GRID_METRICS,
  WIDGET_DATA,
  WIDGET_DATA_EXPORT,
  WIDGET_DATA_V2_MINT2,
} from '../shared/urls.js';
const CompanyInfoSchema = z.object({
  name: z.string(),
  companyId: z.coerce.string(),
  firstDate: z.string(),
});
export type CompanyInfoResponseType = z.infer<typeof CompanyInfoSchema>;
const reportingQueryBase = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnWindowFocus: false,
} as const;

export const FetchDashboardsMint2 = defaultApi.declareEndpoint({
  url: BFF_DASHBOARDS_V2,
  method: 'GET',
  responseSchema: DashboardsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const fetchDashboardsMint2QueryOptions = makeQueryOptions(
  FetchDashboardsMint2,
  {
    processResponse(data) {
      return data;
    },
  },
  reportingQueryBase,
);

export const FetchDashboards = defaultApi.declareEndpoint({
  url: BFF_DASHBOARDS,
  method: 'GET',
  responseSchema: DashboardsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const fetchDashboardsQueryOptions = makeQueryOptions(
  FetchDashboards,
  {
    processResponse(data) {
      return data;
    },
  },
  reportingQueryBase,
);

export const FetchCompanyInfo = defaultApi.declareEndpoint({
  url: COMPANY_INFO,
  method: 'GET',
  responseSchema: CompanyInfoSchema,
});

export const fetchCompanyInfoQueryOptions = makeQueryOptions(
  FetchCompanyInfo,
  {
    processResponse(data) {
      return data;
    },
  },
  reportingQueryBase,
);

export const FetchViews = defaultApi.declareEndpoint({
  url: DASHBOARDS,
  method: 'GET',
  responseSchema: ViewsResponseSchema,
  querySchema: ViewsQuerySchema,
});

export const CreateView = defaultApi.declareEndpoint({
  url: DASHBOARDS,
  method: 'POST',
  requestSchema: CreateViewRequestSchema,
  responseSchema: ViewsResponseSchema,
});

export const FetchViewById = defaultApi.declareEndpoint({
  url: DASHBOARDS_INSIGHTS_HUB_VIEW_BY_ID,
  method: 'GET',
  responseSchema: ViewsResponseSchema,
  querySchema: ViewQuerySchema,
});

export const DeleteView = defaultApi.declareEndpoint({
  url: DASHBOARDS_INSIGHTS_HUB_VIEW_BY_ID,
  method: 'DELETE',
  responseSchema: DeleteViewResponseSchema,
});

export const UpdateView = defaultApi.declareEndpoint({
  url: DASHBOARDS_INSIGHTS_HUB_VIEW_BY_ID,
  method: 'PATCH',
  requestSchema: UpdateViewRequestSchema,
  responseSchema: ViewsResponseSchema,
  querySchema: ViewQuerySchema,
});

export const GetSharedUsers = defaultApi.declareEndpoint({
  url: DASHBOARDS_INSIGHTS_HUB_VIEW_SHARE_BY_ID,
  method: 'GET',
  responseSchema: SharedUsersResponseSchema,
});

export const UpdateSharedUsers = defaultApi.declareEndpoint({
  url: DASHBOARDS_INSIGHTS_HUB_VIEW_SHARE_BY_ID,
  method: 'PUT',
  requestSchema: UpdatePermissionRequestSchema,
  responseSchema: SharedUsersResponseSchema,
});

export const getDashboardsList = defaultClient.query({
  url: BFF_DASHBOARDS,
  method: 'GET',
  responseSchema: DashboardsResponseSchema,
  keyPrefix: ['dashboards', 'v1'],
  processResponse(data) {
    return data.data;
  },
});

export const getDashboardsListLegacy = defaultClient.query({
  url: BFF_DASHBOARDS,
  method: 'GET',
  responseSchema: DashboardsResponseSchema,
  keyPrefix: ['dashboards', 'v1'],
  processResponse(data) {
    return data.data;
  },
});

export const CreateDashboard = defaultApi.declareEndpoint({
  url: DASHBOARDS,
  method: 'POST',
  requestSchema: CreateDashboardRequestSchema,
  responseSchema: DashboardsResponseSchema,
});

export const CreateDashboardMint2 = defaultApi.declareEndpoint({
  url: DASHBOARDS_V2_VIEW,
  method: 'POST',
  requestSchema: CreateDashboardRequestSchema,
  responseSchema: DashboardsResponseSchema,
});

export const UpdateDashboard = defaultApi.declareEndpoint({
  url: DASHBOARDS_INSIGHTS_HUB_VIEW_BY_DASHBOARD_ID,
  method: 'PATCH',
  requestSchema: UpdateDashboardRequestSchema,
  responseSchema: DashboardsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const DeleteDashboard = defaultApi.declareEndpoint({
  url: DASHBOARDS_INSIGHTS_HUB_VIEW_BY_DASHBOARD_ID,
  method: 'DELETE',
  responseSchema: DeleteDashboardResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const GetDashboardsListSettings = defaultApi.declareEndpoint({
  url: DASHBOARDS_SETTINGS,
  method: 'GET',
  responseSchema: DashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const fetchDashboardsListSettingsQueryOptions = makeQueryOptions(
  GetDashboardsListSettings,
  {
    processResponse(data) {
      return data.data.items[0] ?? null;
    },
  },
  reportingQueryBase,
);

export const GetPivotListSettings = defaultApi.declareEndpoint({
  url: DASHBOARDS_SETTINGS,
  method: 'GET',
  responseSchema: PivotListSettingsResponseSchema,
  querySchema: PivotTypeQuerySchema,
});

export const fetchPivotListSettingsQueryOptions = makeQueryOptions(
  GetPivotListSettings,
  {
    processResponse(data) {
      return data.data.items[0] ?? null;
    },
  },
  reportingQueryBase,
);

export const CreatePivotListSettings = defaultApi.declareEndpoint({
  url: DASHBOARDS_SETTINGS,
  method: 'POST',
  requestSchema: CreatePivotListSettingsRequestSchema,
  responseSchema: PivotListSettingsResponseSchema,
});

export const EditPivotListSettings = defaultApi.declareEndpoint({
  url: DASHBOARDS_SETTINGS,
  method: 'PATCH',
  requestSchema: PivotSettingsParamsSchema,
  responseSchema: PivotListSettingsResponseSchema,
  querySchema: PivotTypeQuerySchema,
});

export const getDashboardsListSettings = defaultClient.query({
  url: DASHBOARDS_SETTINGS,
  method: 'GET',
  responseSchema: DashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
  keyPrefix: ['dashboards', 'settings'],
  processResponse(data) {
    return data.data.items[0] ?? null;
  },
});

export const CreateDashboardsListSettings = defaultApi.declareEndpoint({
  url: DASHBOARDS_SETTINGS,
  method: 'POST',
  requestSchema: CreateDashboardsListSettingsRequestSchema,
  responseSchema: DashboardsListSettingsResponseSchema,
});

export const createDashboardsListSettings = defaultClient.mutation({
  url: DASHBOARDS_SETTINGS,
  method: 'POST',
  requestSchema: CreateDashboardsListSettingsRequestSchema,
  responseSchema: DashboardsListSettingsResponseSchema,
  processResponse(data) {
    return data.data.items[0];
  },
  onSuccess(queryClient) {
    getDashboardsListSettings.invalidateAll(queryClient, {});
  },
});

export const EditDashboardsListSettings = defaultApi.declareEndpoint({
  url: DASHBOARDS_SETTINGS,
  method: 'PATCH',
  requestSchema: DashboardSettingsParamsSchema,
  responseSchema: DashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const editDashboardsListSettings = defaultClient.mutation({
  url: DASHBOARDS_SETTINGS,
  method: 'PATCH',
  requestSchema: DashboardSettingsParamsSchema,
  responseSchema: DashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
  processResponse(data) {
    return data.data.items[0];
  },
  onSuccess(queryClient) {
    getDashboardsListSettings.invalidateAll(queryClient, {});
  },
});

export const DeleteDashboardsListSettings = defaultApi.declareEndpoint({
  url: DASHBOARDS_SETTINGS,
  method: 'DELETE',
  responseSchema: DeleteDashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const deleteDashboardsListSettings = defaultClient.mutation({
  url: DASHBOARDS_SETTINGS,
  method: 'DELETE',
  responseSchema: DeleteDashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
  processResponse(data) {
    return data;
  },
  onSuccess(queryClient) {
    getDashboardsListSettings.invalidateAll(queryClient, {});
  },
});

export const GetDashboardsListSettingsMint2 = defaultApi.declareEndpoint({
  url: DASHBOARDS_V2_SETTINGS,
  method: 'GET',
  responseSchema: DashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const fetchDashboardsListSettingsMint2QueryOptions = makeQueryOptions(
  GetDashboardsListSettingsMint2,
  {
    processResponse(data) {
      return data.data.items[0] ?? null;
    },
  },
  reportingQueryBase,
);

export const CreateDashboardsListSettingsMint2 = defaultApi.declareEndpoint({
  url: DASHBOARDS_V2_SETTINGS,
  method: 'POST',
  requestSchema: CreateDashboardsListSettingsRequestSchema,
  responseSchema: DashboardsListSettingsResponseSchema,
});

export const EditDashboardsListSettingsMint2 = defaultApi.declareEndpoint({
  url: DASHBOARDS_V2_SETTINGS,
  method: 'PATCH',
  requestSchema: DashboardSettingsParamsSchema,
  responseSchema: DashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const DeleteDashboardsListSettingsMint2 = defaultApi.declareEndpoint({
  url: DASHBOARDS_V2_SETTINGS,
  method: 'DELETE',
  responseSchema: DeleteDashboardsListSettingsResponseSchema,
  querySchema: DashboardTypeQuerySchema,
});

export const FetchWidgetDataInternal = defaultApi.declareEndpoint({
  url: WIDGET_DATA,
  method: 'POST',
  requestSchema: WidgetDataRequestSchemaSnakeCase,
  responseSchema: WidgetDataResponseSchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

export const FetchWidgetDataInternalMint2 = defaultApi.declareEndpoint({
  url: WIDGET_DATA_V2_MINT2,
  method: 'POST',
  requestSchema: WidgetDataRequestSchemaSnakeCase,
  responseSchema: WidgetDataResponseSchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

//! TODO: REFACTOR
export const fetchWidgetDataQueryOptions = (
  args: {
    data: Record<string, unknown>;
  },
  isReportingMint2 = false,
) => {
  // Create unique key from critical request parameters
  const uniqueKey = JSON.stringify({
    companyId: args.data.company_id,
    metrics: args.data.metrics,
    dimensions: args.data.dimensions,
    dateFrom: args.data.date_from,
    dateTo: args.data.date_to,
    periodId: args.data.period_id,
    comparisonDateFrom: args.data.comparison_date_from,
    comparisonDateTo: args.data.comparison_date_to,
    metricScale: args.data.metric_scale,
    dataBucketing: args.data.data_bucketing,
    missingValuesFill: args.data.missing_values_fill,
    aggregationMetrics: args.data.aggregation_metrics,
    currencyName: args.data.currency_name,
    chartType: args.data.chart_type,
    filters: args.data.dimension_filters,
  });

  // The `args.data` is loosely typed as `Record<string, unknown>` at this TODO-refactor
  // boundary; the endpoint's request schema narrows to a concrete shape. Cast to `any`
  // to bridge the loose caller type with the inferred request body.
  const baseQueryOptions = makeQueryOptions(
    FetchWidgetDataInternal,
    {
      keySuffix: [uniqueKey],
      processResponse(data) {
        return data;
      },
    },
    reportingQueryBase,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see note above
  )(args as any);

  if (isReportingMint2) {
    return makeQueryOptions(
      FetchWidgetDataInternalMint2,
      {
        keySuffix: [uniqueKey],
        processResponse(data) {
          return data;
        },
      },
      reportingQueryBase,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see note above
    )(args as any) as unknown as typeof baseQueryOptions;
  }

  return baseQueryOptions;
};

export const ExportWidgetDataCsvInternal = defaultApi.declareEndpoint({
  url: WIDGET_DATA_EXPORT,
  method: 'POST',
  requestSchema: WidgetDataRequestSchemaSnakeCase,
  responseSchema: WidgetDataExportResponseSchema,
});

export const ExportWidgetDataCsvInternalMint2 = defaultApi.declareEndpoint({
  url: EXPORT_CSV_V2_MINT2,
  method: 'POST',
  requestSchema: WidgetDataRequestSchemaSnakeCase,
  responseSchema: WidgetDataExportResponseSchema,
});

export const FetchPivotTableDataLegacy = defaultApi.declareEndpoint({
  url: GET_TABLE_OUTDATED,
  method: 'POST',
  requestSchema: PivotTableRequestSchema,
  responseSchema: PivotTableResponseSchema,
});

export const fetchPivotTableDataLegacyQueryOptions = makeQueryOptions(
  FetchPivotTableDataLegacy,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const FetchPivotTableDataV2 = defaultApi.declareEndpoint({
  url: GET_TABLE,
  method: 'POST',
  requestSchema: PivotTableRequestSchema,
  responseSchema: PivotTableResponseSchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

const PivotTableQuerySchema = z.object({
  isId: z.boolean().default(true),
});

export const FetchPivotTableDataLegacyForPivot = defaultApi.declareEndpoint({
  url: GET_TABLE_OUTDATED,
  method: 'POST',
  requestSchema: PivotTableRequestSchema,
  responseSchema: PivotTableResponseSchema,
  querySchema: PivotTableQuerySchema,
});

export const FetchPivotTableDataV2ForPivot = defaultApi.declareEndpoint({
  url: GET_TABLE,
  method: 'POST',
  requestSchema: PivotTableRequestSchema,
  responseSchema: PivotTableResponseSchema,
  querySchema: PivotTableQuerySchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

export const fetchPivotTableDataV2QueryOptions = makeQueryOptions(
  FetchPivotTableDataV2,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const FetchPivotTableDataV2Mint2 = defaultApi.declareEndpoint({
  url: GET_TABLE_V2_MINT2,
  method: 'POST',
  requestSchema: PivotTableRequestSchema,
  responseSchema: PivotTableResponseSchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

export const fetchPivotTableDataV2Mint2QueryOptions = makeQueryOptions(
  FetchPivotTableDataV2Mint2,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const FetchPivotComparisonTableLegacy = defaultApi.declareEndpoint({
  url: GET_COMPARISON_TABLE_OUTDATED,
  method: 'POST',
  requestSchema: PivotTableRequestSchema,
  responseSchema: PivotTableResponseSchema,
});

export const fetchPivotComparisonTableLegacyQueryOptions = makeQueryOptions(
  FetchPivotComparisonTableLegacy,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const FetchPivotComparisonTableV2 = defaultApi.declareEndpoint({
  url: GET_COMPARISON_TABLE,
  method: 'POST',
  requestSchema: PivotTableComparisonRequestSchema,
  responseSchema: PivotTableResponseSchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

export const FetchPivotComparisonTableLegacyForPivot =
  defaultApi.declareEndpoint({
    url: GET_COMPARISON_TABLE_OUTDATED,
    method: 'POST',
    requestSchema: PivotTableRequestSchema,
    responseSchema: PivotTableResponseSchema,
    querySchema: PivotTableQuerySchema,
  });

export const FetchPivotComparisonTableV2ForPivot = defaultApi.declareEndpoint({
  url: GET_COMPARISON_TABLE,
  method: 'POST',
  requestSchema: PivotTableComparisonRequestSchema,
  responseSchema: PivotTableResponseSchema,
  querySchema: PivotTableQuerySchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

export const FetchPivotComparisonTableV2Mint2 = defaultApi.declareEndpoint({
  url: GET_COMPARISON_TABLE_V2_MINT2,
  method: 'POST',
  requestSchema: PivotTableComparisonRequestSchema,
  responseSchema: PivotTableResponseSchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

export const ExportPivotComparisonTableCsv = {
  url: GET_COMPARISON_TABLE,
};

export const fetchPivotComparisonTableV2QueryOptions = makeQueryOptions(
  FetchPivotComparisonTableV2,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const fetchPivotComparisonTableV2Mint2QueryOptions = makeQueryOptions(
  FetchPivotComparisonTableV2Mint2,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const ExportPivotTableCsv = {
  url: GET_TABLE,
};

export const ExportPivotTableCsvMint2 = {
  url: GET_TABLE_V2_MINT2,
};

export const ExportPivotComparisonTableCsvMint2 = {
  url: GET_COMPARISON_TABLE_V2_MINT2,
};

export const FetchPivotMetricsMint2 = defaultApi.declareEndpoint({
  url: GET_METRICS_V2_MINT2,
  method: 'GET',
  responseSchema: PivotMetricsResponseSchema,
});

export const fetchPivotMetricsMint2QueryOptions = makeQueryOptions(
  FetchPivotMetricsMint2,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchPivotDimensionsMint2 = defaultApi.declareEndpoint({
  url: GET_DIMENSIONS_V2_MINT2,
  method: 'GET',
  responseSchema: PivotDimensionsResponseSchema,
});

export const fetchPivotDimensionsMint2QueryOptions = makeQueryOptions(
  FetchPivotDimensionsMint2,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchPivotDimensionUniquesMint2 = defaultApi.declareEndpoint({
  url: GET_DIMENSIONS_UNIQUES_V2_MINT2,
  method: 'POST',
  requestSchema: PivotDimensionUniquesRequestSchema,
  responseSchema: PivotDimensionUniquesResponseSchema,
});

export const FetchPivotCurrenciesMint2 = defaultApi.declareEndpoint({
  url: GET_CURRENCIES_V2_MINT2,
  method: 'GET',
  responseSchema: PivotCurrenciesResponseSchema,
});

export const fetchPivotCurrenciesMint2QueryOptions = makeQueryOptions(
  FetchPivotCurrenciesMint2,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchCompanyInfoMint2 = defaultApi.declareEndpoint({
  url: COMPANY_INFO_V2_MINT2,
  method: 'GET',
  responseSchema: z.object({
    name: z.string(),
    companyId: z.coerce.string(),
    firstDate: z.string(),
  }),
});

export const fetchCompanyInfoMint2QueryOptions = makeQueryOptions(
  FetchCompanyInfoMint2,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchPivotMetrics = defaultApi.declareEndpoint({
  url: GET_METRICS,
  method: 'GET',
  responseSchema: PivotMetricsResponseSchema,
});

export const fetchPivotMetricsQueryOptions = makeQueryOptions(
  FetchPivotMetrics,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchPivotDimensions = defaultApi.declareEndpoint({
  url: GET_DIMENSIONS_V2,
  method: 'GET',
  responseSchema: PivotDimensionsResponseSchema,
});

export const fetchPivotDimensionsQueryOptions = makeQueryOptions(
  FetchPivotDimensions,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchPivotReachSubRows = defaultApi.declareEndpoint({
  url: GET_REACH_SUBROWS,
  method: 'POST',
  requestSchema: PivotReachSubRowsRequestSchema,
  responseSchema: PivotReachSubRowsResponseSchema,
});

export const fetchPivotReachSubRowsQueryOptions = makeQueryOptions(
  FetchPivotReachSubRows,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const FetchPivotReachSubRowsForPivot = defaultApi.declareEndpoint({
  url: GET_REACH_SUBROWS,
  method: 'POST',
  requestSchema: PivotReachSubRowsRequestSchema,
  responseSchema: PivotReachSubRowsResponseSchema,
  querySchema: PivotTableQuerySchema,
});

export const FetchPivotFrequencySubRows = defaultApi.declareEndpoint({
  url: GET_FREQUENCY_SUBROWS,
  method: 'POST',
  requestSchema: PivotReachSubRowsRequestSchema,
  responseSchema: PivotFrequencySubRowsResponseSchema,
});

export const FetchPivotFrequencySubRowsForPivot = defaultApi.declareEndpoint({
  url: GET_FREQUENCY_SUBROWS,
  method: 'POST',
  requestSchema: PivotReachSubRowsRequestSchema,
  responseSchema: PivotFrequencySubRowsResponseSchema,
  querySchema: PivotTableQuerySchema,
});

export const fetchPivotFrequencySubRowsQueryOptions = makeQueryOptions(
  FetchPivotFrequencySubRows,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const FetchUserCurrency = defaultApi.declareEndpoint({
  url: GET_SELECTED_CURRENCY,
  method: 'GET',
  responseSchema: UserCurrencyResponseSchema,
});

export const fetchUserCurrencyQueryOptions = makeQueryOptions(
  FetchUserCurrency,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchPivotCurrencies = defaultApi.declareEndpoint({
  url: GET_USER_CURRENCIES,
  method: 'GET',
  responseSchema: PivotCurrenciesResponseSchema,
});

export const fetchPivotCurrenciesQueryOptions = makeQueryOptions(
  FetchPivotCurrencies,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  },
);

export const FetchPivotDimensionUniques = defaultApi.declareEndpoint({
  url: DIMENSIONS_UNIQUES,
  method: 'POST',
  requestSchema: PivotDimensionUniquesRequestSchema,
  responseSchema: PivotDimensionUniquesResponseSchema,
});

// New Export for MINT v2

const PivotTableExportQuerySchema = z.object({
  exportType: z.enum(['csv', 'xlsx', 'json', 'tsv']),
});

export const ExportPivotTableDataV2 = defaultApi.declareEndpoint({
  url: EXPORT_PIVOT_V2_MINT2,
  method: 'POST',
  requestSchema: PivotTableExportRequestSchema,
  responseSchema: PivotTableExportResponseSchema,
  querySchema: PivotTableExportQuerySchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

// Export functions for pivot table CSV export (blob response)
export type ExportPivotTableFileType =
  | 'csv'
  | 'xlsx'
  | 'json'
  | 'tsv'
  | 'xlsxFlowchart';

type ExportPivotTableResponse = {
  blob: Blob;
  contentType: string;
  fileName?: string;
};

const getHeader = (
  headers: Headers | Record<string, string>,
  name: string,
): string | undefined => {
  if (headers instanceof Headers) {
    return headers.get(name) || undefined;
  }
  return headers[name.toLowerCase()] || headers[name];
};

const createExportResponse = (response: {
  headers: Headers | Record<string, string>;
  request: { response: unknown };
}): ExportPivotTableResponse => {
  const contentDisposition = getHeader(response.headers, 'content-disposition');
  let fileName;

  if (contentDisposition) {
    const FILENAME_REGEX = /filename="?([^"]+)"?/;
    const match = FILENAME_REGEX.exec(contentDisposition);
    fileName = match?.[1]?.replaceAll('_', ' ');
  }

  const headerContentType = getHeader(response.headers, 'content-type');
  const contentType = headerContentType?.toString().split(';')[0] || 'text/csv';

  // @ts-expect-error TS2339 There is issue with blob file and dataToCamelCase interceptor
  const blob = new Blob([response.request.response], {
    type: contentType,
  });

  return {
    blob,
    contentType,
    fileName,
  };
};

const exportPivotTableBlob = async (
  url: string,
  fileType: ExportPivotTableFileType,
  payload: unknown,
  schema: z.ZodSchema,
  params?: Record<string, unknown>,
): Promise<ExportPivotTableResponse> => {
  const validatedPayload = schema.parse(payload);
  const client = defaultApi.getClient();

  // @ts-expect-error TS2339 There is issue with blob file and dataToCamelCase interceptor
  const response = (await client.request({
    url: `${API_PREFIX}/${url}`,
    method: 'POST',
    data: validatedPayload,
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
    },
    params: {
      format: fileType,
      ...params,
    },
  })) as {
    headers: Headers | Record<string, string>;
    request: { response: unknown };
  };

  return createExportResponse(response);
};

export const exportPivotTableCsv = async (
  fileType: ExportPivotTableFileType,
  payload: z.infer<typeof PivotTableRequestSchema>,
  isReportingMint2 = false,
): Promise<ExportPivotTableResponse> => {
  const exportUrl = isReportingMint2
    ? ExportPivotTableCsvMint2.url
    : ExportPivotTableCsv.url;

  return exportPivotTableBlob(
    exportUrl,
    fileType,
    payload,
    PivotTableRequestSchema,
  );
};

export const exportPivotComparisonTableCsv = async (
  fileType: ExportPivotTableFileType,
  payload: z.infer<typeof PivotTableComparisonRequestSchema>,
  isReportingMint2 = false,
): Promise<ExportPivotTableResponse> => {
  const exportUrl = isReportingMint2
    ? ExportPivotComparisonTableCsvMint2.url
    : ExportPivotComparisonTableCsv.url;

  return exportPivotTableBlob(
    exportUrl,
    fileType,
    payload,
    PivotTableComparisonRequestSchema,
  );
};

export const exportPivotTableCsvV2ForPivot = async (
  fileType: ExportPivotTableFileType,
  payload: z.infer<typeof PivotTableRequestSchema>,
): Promise<ExportPivotTableResponse> => {
  return exportPivotTableV2(fileType, payload);
};

export const exportPivotComparisonTableCsvV2ForPivot = async (
  fileType: ExportPivotTableFileType,
  payload: z.infer<typeof PivotTableComparisonRequestSchema>,
): Promise<ExportPivotTableResponse> => {
  return exportPivotTableBlob(
    GET_COMPARISON_TABLE,
    fileType,
    payload,
    PivotTableComparisonRequestSchema,
  );
};

export const exportPivotTableFlowChart = async (
  payload: z.infer<typeof ExportPivotTableFlowChartRequestSchema>,
): Promise<ExportPivotTableResponse> => {
  return exportPivotTableBlob(
    EXPORT_TABLE_FLOWCHART,
    'xlsxFlowchart',
    payload,
    ExportPivotTableFlowChartRequestSchema,
  );
};

export const exportPivotTableV2 = async (
  fileType: ExportPivotTableFileType,
  payload: z.infer<typeof PivotTableRequestSchema>,
): Promise<ExportPivotTableResponse> => {
  return exportPivotTableBlob(
    GET_TABLE,
    fileType,
    payload,
    PivotTableRequestSchema,
  );
};

// AG Grid

export const FetchGridData = defaultApi.declareEndpoint({
  url: GRID_DATA,
  method: 'POST',
  requestSchema: GridDataRequestSchema,
  responseSchema: GridDataResponseSchema,
  clientOptions: {
    response: {
      camelCaseBody: false,
    },
    request: {
      snakeCaseBody: false,
    },
  },
});

export const fetchGridDataQueryOptions = makeQueryOptions(FetchGridData, {
  processResponse(data) {
    return data;
  },
});

export const FetchGridDimensions = defaultApi.declareEndpoint({
  url: GRID_DIMENSIONS,
  method: 'GET',
  responseSchema: GridDimensionsResponseSchema,
});

export const fetchGridDimensionsQueryOptions = makeQueryOptions(
  FetchGridDimensions,
  {
    processResponse(data) {
      return data;
    },
  },
  reportingQueryBase,
);

export const FetchGridMetrics = defaultApi.declareEndpoint({
  url: GRID_METRICS,
  method: 'GET',
  responseSchema: GridMetricsResponseSchema,
});

export const fetchGridMetricsQueryOptions = makeQueryOptions(
  FetchGridMetrics,
  {
    processResponse(data) {
      return data;
    },
  },
  reportingQueryBase,
);
